import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
// Cookie-first auth (httpOnly `access_token`). A bearer fallback is kept for deployments where the
// storefront domain differs from the API domain (SameSite cookies are not sent cross-site there).
const cred = { withCredentials: true };
const TOKEN_KEY = "nura_token";
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && (config.url || "").startsWith(API) && !config.headers?.Authorization) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Returning from Google sign-in: AuthCallback exchanges the session_id first, skip the /me check
    if (window.location.hash?.includes("session_id=")) { setUser(false); return; }
    axios.get(`${API}/auth/me`, cred).then((r) => setUser(r.data)).catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(false); });
  }, []);

  // A page restored from the back/forward cache keeps its rendered DOM: reload so the
  // session is checked again and a logged-out visitor never sees admin/account content.
  useEffect(() => {
    const onShow = (e) => { if (e.persisted) window.location.reload(); };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  const onAuth = (data) => {
    if (data.requires_2fa) return data;
    if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser({ email: data.email, name: data.name, role: data.role, twofa_enabled: data.twofa_enabled });
    setTimeout(() => refreshUser(), 300);
    return data;
  };

  const login = async (email, password) => onAuth((await axios.post(`${API}/auth/login`, { email, password }, cred)).data);
  const register = async (name, email, password, newsletter = false) => onAuth((await axios.post(`${API}/auth/register`, { name, email, password, newsletter }, cred)).data);
  const verify2fa = async (pre_token, code) => onAuth((await axios.post(`${API}/auth/2fa/verify`, { pre_token, code }, cred)).data);
  const googleSession = async (sessionId) => onAuth((await axios.post(`${API}/auth/google/session`, { session_id: sessionId }, cred)).data);
  const refreshUser = () => axios.get(`${API}/auth/me`, cred).then((r) => setUser(r.data)).catch(() => {});

  const logout = async () => {
    const wasAdmin = user?.role === "admin";
    setUser(false);
    localStorage.removeItem(TOKEN_KEY);
    try { await axios.post(`${API}/auth/logout`, {}, cred); }
    catch (e) { console.warn("logout: session already closed", e?.response?.status); }
    // Hard replace: drops the current history entry so the back button cannot restore
    // the authenticated view, and forces a fresh session check on the login screen.
    window.location.replace(wasAdmin ? "/admin" : "/compte");
  };

  return <AuthContext.Provider value={{ user, login, register, logout, verify2fa, refreshUser, googleSession }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
